import { useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import Sentry from "@similarweb/sentry-logger";
import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Stack, Text, Box } from "@similarweb/ui-components/foundations";

import { getChosenPageItems } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import MetricsCompareBars from "app/components/React/MetricsCompareBars/MetricsCompareBars";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import { DefaultFetchService } from "app/services/fetchService";
import BoxTitle from "pro-features/components/BoxTitle/src/BoxTitle";

import { useWebsiteKeywordsEndpoints } from "../websiteKeywordsEndpoints";
import { useWebsiteKeywordsPage2Context } from "../WebsiteKeywordsPage2Context";
import { getItemsFromBreakdown } from "../WebsiteKeywordsPageUtillities2";

import { metricBarNameMap, PERCENT } from "./consts";
import type { IData, IItem } from "./types";

const fetchService = DefaultFetchService.getInstance();

const metricsBarsContainerStyles = {
    width: "100%",
    borderRadius: "6px",
    background: colorsPalettes.carbon[0],
    margin: "24px 0 0",
    padding: "24px",
};

export const WebsiteKeywordsPage2CompareBar = () => {
    const { requestBody, apiParams } = useWebsiteKeywordsPage2Context();
    const { pageFilter, key } = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();
    const filtersForRequest = Object.fromEntries(Object.entries(apiParams).filter(([, value]) => value != null));
    const sitesList = getChosenPageItems(JSON.parse(pageFilter ?? "[]"));

    const transformData = (data: IData) => {
        return {
            traffic_share: Object.fromEntries(
                Object.entries(data?.Data.BreakDown).map(([key, value]) => [
                    key,
                    {
                        value: value.Value * data?.Data.BreakDown[key].Percentage,
                        percentage: data?.Data.BreakDown[key].Percentage,
                    },
                ]),
            ),
        };
    };

    const orderData = (data: IData) => {
        const transformedData = transformData(data);
        const metricName = "traffic_share";
        const orderedTrafficShare: Record<string, Record<string, number>> = sitesList.reduce((agr, cur) => {
            const itemName = cur.url;
            const itemData = transformedData[metricName][itemName];
            if (itemData) {
                agr[metricName] = {
                    ...agr[metricName],
                    [itemName]: itemData,
                };
            }
            return agr;
        }, {});
        return orderedTrafficShare;
    };

    const { data: items, isLoading } = useQuery({
        queryKey: [
            "WebsiteKeywordsPage2CompareBar",
            endpoints.trafficShare,
            requestBody,
            ...Object.values(filtersForRequest),
            key,
        ],
        queryFn: () =>
            fetchService.post<IData>(
                `${endpoints.trafficShare}?${queryString.stringify(filtersForRequest)}`,
                requestBody,
            ),
        select: (data): IItem[] | null => {
            try {
                if (data) {
                    return getItemsFromBreakdown(
                        orderData(data),
                        sitesList,
                        PERCENT,
                        minVisitsAbbrFilter,
                        metricBarNameMap,
                    ) as IItem[];
                }
                return null;
            } catch (error) {
                Sentry.error("Error in select function", error);
                return null;
            }
        },
    });

    return isLoading ? (
        <Box {...metricsBarsContainerStyles}>
            <PixelPlaceholderLoader
                width={"100%"}
                height={20}
            />
        </Box>
    ) : items ? (
        <Stack {...metricsBarsContainerStyles}>
            <BoxTitle tooltip={translate("website.keywords.compare.bar.tooltip")}>
                <Text
                    fontVariant="input2"
                    color={colorsPalettes.carbon[500]}
                    margin="0px 0px 16px"
                >
                    {translate("website.keywords.compare.bar.title")}
                </Text>
            </BoxTitle>
            <MetricsCompareBars
                isFetching={isLoading}
                items={items}
                title={translate("analysis.source.search.keywords.header.split.tooltip.title")}
            />
        </Stack>
    ) : null;
};
